"use client";

import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Ellipsis,
  Link2,
  Loader2,
  LockKeyhole,
  MessageSquare,
  Save,
  Send,
  Trash2,
  Unlink2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useStore } from "../../lib/store";
import type { AdminSlackLinkVerification } from "../../lib/slackAdminLink";
import { normalizeSlackMemberReference } from "../../lib/slackIdentity";
import { formatRelative } from "../../lib/utils";

export type WorkspaceAccountState =
  | "provisioned"
  | "invited"
  | "active"
  | "suspended";

export type WorkspaceHumanRole = "owner" | "admin" | "member" | "viewer";

export interface WorkspaceSlackIdentity {
  slack_team_id: string;
  slack_user_id: string;
  display_name: string | null;
  email: string | null;
  link_source: "admin" | "verified_email" | "self_link";
}

export interface WorkspaceAccount {
  user_id: string;
  name: string;
  email: string;
  role: WorkspaceHumanRole;
  account_state: WorkspaceAccountState;
  email_confirmed: boolean;
  activation_sent_at: string | null;
  last_sign_in_at: string | null;
  can_edit_email: boolean;
  can_send_setup: boolean;
  can_send_reset: boolean;
  team_ids: string[];
  slack_identity: WorkspaceSlackIdentity | null;
  can_link_slack: boolean;
}

export interface WorkspaceAccountTeam {
  id: string;
  name: string;
  icon: string;
}

type Flash = { kind: "success" | "error"; message: string };
type BusyAction =
  | "create"
  | "details"
  | "role"
  | "teams"
  | "slack"
  | "activation"
  | "password-reset"
  | "remove";

const ROLE_LABELS: Record<WorkspaceHumanRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

const ROLE_DESCRIPTIONS: Record<Exclude<WorkspaceHumanRole, "owner">, string> = {
  admin: "Can manage people, settings, and all workspace content.",
  member: "Can create and edit workspace content.",
  viewer: "Can view the workspace without making changes.",
};

const ACCOUNT_STATE_PRESENTATION: Record<
  WorkspaceAccountState,
  { label: string; className: string }
> = {
  provisioned: {
    label: "Setup not sent",
    className:
      "border-[var(--border-strong)] bg-[var(--bg-tertiary)] text-[var(--fg-secondary)]",
  },
  invited: {
    label: "Setup sent",
    className: "border-[var(--blue)]/40 bg-[var(--blue)]/10 text-[var(--blue)]",
  },
  active: {
    label: "Active",
    className: "border-[var(--green)]/40 bg-[var(--green)]/10 text-[var(--green)]",
  },
  suspended: {
    label: "Suspended",
    className: "border-[var(--red)]/40 bg-[var(--red)]/10 text-[var(--red)]",
  },
};

function initialsFor(name: string, email: string) {
  const source = name.trim() || email.split("@")[0] || "?";
  return source
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2);
}

function accountTimeline(account: WorkspaceAccount) {
  if (account.account_state === "active" && account.last_sign_in_at) {
    const lastSignIn = Date.parse(account.last_sign_in_at);
    return Number.isNaN(lastSignIn)
      ? "Has signed in"
      : `Last signed in ${formatRelative(lastSignIn)}`;
  }
  if (account.account_state === "invited" && account.activation_sent_at) {
    const activationSent = Date.parse(account.activation_sent_at);
    return Number.isNaN(activationSent)
      ? "Setup email sent"
      : `Setup sent ${formatRelative(activationSent)}`;
  }
  if (account.account_state === "suspended") return "Sign-in is disabled";
  return "Can receive Tasks before signing in";
}

function accountErrorMessage(error: string | undefined, detail?: string) {
  const messages: Record<string, string> = {
    unauthorized: "Sign in again to manage workspace accounts.",
    forbidden: "Only workspace admins can manage accounts.",
    invalid_email: "Enter a valid email address.",
    invalid_role: "Choose Admin, Member, or Viewer access.",
    invalid_team_ids: "Choose only teams from this workspace.",
    invalid_slack_identity: "Paste a Slack member ID or profile link.",
    slack_not_installed: "Slack is not connected to this workspace.",
    slack_user_not_found:
      "That ID belongs to another Slack workspace and is not marked as a Slack Connect guest.",
    slack_user_not_human: "Choose a human Slack member, not a bot or app.",
    slack_lookup_failed: "Slack could not verify that member. Try again.",
    slack_exact_id_confirmation_required:
      "Slack could not verify this exact ID. Confirm the Slack Connect fallback to continue.",
    slack_identity_in_use:
      "That Slack member is already linked to another Pauta account.",
    pauta_account_already_linked:
      "Unlink the current Slack account before linking another one.",
    already_member: "That person already belongs to this workspace.",
    email_in_use: "That email already belongs to another Pauta account.",
    not_managed: "This account cannot be changed from this workspace.",
    rate_limited: "Too many emails were requested. Wait a moment and try again.",
  };
  return messages[error ?? ""] ?? detail ?? "Something went wrong. Try again.";
}

export function AccountStatusBadge({ state }: { state: WorkspaceAccountState }) {
  const presentation = ACCOUNT_STATE_PRESENTATION[state];
  return (
    <span
      data-account-state={state}
      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10.5px] font-medium ${presentation.className}`}
    >
      {presentation.label}
    </span>
  );
}

export function WorkspaceAccountRow({
  account,
  expanded,
  busyAction,
  editName,
  editEmail,
  slackReference,
  teams,
  onToggle,
  onEditName,
  onEditEmail,
  onEditSlackReference,
  onSaveDetails,
  onChangeRole,
  onChangeTeams,
  onLinkSlack,
  onUnlinkSlack,
  onSendSetup,
  onSendPasswordReset,
  onRemove,
}: {
  account: WorkspaceAccount;
  expanded: boolean;
  busyAction: BusyAction | null;
  editName: string;
  editEmail: string;
  slackReference: string;
  teams: WorkspaceAccountTeam[];
  onToggle: () => void;
  onEditName: (value: string) => void;
  onEditEmail: (value: string) => void;
  onEditSlackReference: (value: string) => void;
  onSaveDetails: () => void;
  onChangeRole: (role: Exclude<WorkspaceHumanRole, "owner">) => void;
  onChangeTeams: (teamIds: string[]) => void;
  onLinkSlack: () => void;
  onUnlinkSlack: () => void;
  onSendSetup: () => void;
  onSendPasswordReset: () => void;
  onRemove: () => void;
}) {
  const isOwner = account.role === "owner";
  const busy = busyAction !== null;
  const detailsChanged =
    editName.trim() !== account.name || editEmail.trim().toLowerCase() !== account.email;
  const normalizedSlackReference = normalizeSlackMemberReference(slackReference);
  const slackLabelId = `slack-account-label-${account.user_id}`;
  const slackHelpId = `slack-account-help-${account.user_id}`;
  const slackInputId = `slack-account-input-${account.user_id}`;

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] overflow-hidden">
      <div className="group flex flex-wrap items-center gap-3 px-3 py-2 hover:bg-[var(--bg-hover)] transition-colors">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[10.5px] font-medium text-white">
          {initialsFor(account.name, account.email)}
        </div>
        <div className="min-w-[160px] flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-[12.5px] font-medium">{account.name}</span>
            <AccountStatusBadge state={account.account_state} />
          </div>
          <div className="truncate font-mono text-[11px] text-[var(--fg-muted)]">
            {account.email}
          </div>
          <div className="mt-0.5 text-[10.5px] text-[var(--fg-tertiary)]">
            {accountTimeline(account)}
          </div>
        </div>

        {isOwner ? (
          <span className="rounded-md border border-[var(--purple)]/40 bg-[var(--purple)]/10 px-1.5 py-0.5 text-[10.5px] font-medium text-[var(--purple)]">
            Owner
          </span>
        ) : (
          <label className="flex items-center gap-1.5">
            <span className="sr-only">Access for {account.name}</span>
            <select
              aria-label={`Access for ${account.name}`}
              value={account.role}
              disabled={busy}
              onChange={(event) =>
                onChangeRole(
                  event.target.value as Exclude<WorkspaceHumanRole, "owner">
                )
              }
              className="rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11.5px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)] disabled:opacity-50"
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
          </label>
        )}

        <button
          type="button"
          aria-label={`Manage ${account.name}`}
          aria-expanded={expanded}
          onClick={onToggle}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--fg-tertiary)] hover:bg-[var(--bg-active)] hover:text-[var(--fg)] transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
          title={`Manage ${account.name}`}
        >
          {expanded ? <X size={13} /> : <Ellipsis size={14} />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-3">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)_auto] md:items-end">
            <label className="block min-w-0">
              <span className="mb-1 block text-[10.5px] font-medium text-[var(--fg-tertiary)]">
                Name
              </span>
              <input
                value={editName}
                disabled={busy}
                onChange={(event) => onEditName(event.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)] disabled:opacity-50"
              />
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block text-[10.5px] font-medium text-[var(--fg-tertiary)]">
                Sign-in email
              </span>
              <input
                type="email"
                value={editEmail}
                disabled={busy || !account.can_edit_email}
                onChange={(event) => onEditEmail(event.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>
            <button
              type="button"
              onClick={onSaveDetails}
              disabled={busy || !editName.trim() || !editEmail.trim() || !detailsChanged}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 transition-colors"
            >
              {busyAction === "details" ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Save size={12} />
              )}
              Save
            </button>
          </div>

          {!account.can_edit_email && (
            <p className="mt-1 text-[10.5px] text-[var(--fg-muted)]">
              Active sign-in emails require a verified email-change flow and cannot be edited here.
            </p>
          )}

          {account.can_link_slack && (
            <div
              className="mt-3 border-t border-[var(--border)] pt-3"
              role="group"
              aria-labelledby={slackLabelId}
              aria-busy={busyAction === "slack"}
            >
              <div className="mb-2 flex items-center gap-1.5">
                <MessageSquare
                  size={12}
                  aria-hidden="true"
                  className="text-[var(--fg-tertiary)]"
                />
                <span
                  id={slackLabelId}
                  className="text-[10.5px] font-medium text-[var(--fg-tertiary)]"
                >
                  Slack account
                </span>
                {!account.slack_identity && busyAction !== "slack" && (
                  <span className="text-[10.5px] text-[var(--fg-muted)]">
                    Not connected
                  </span>
                )}
                {busyAction === "slack" && (
                  <Loader2
                    size={11}
                    className="animate-spin text-[var(--fg-muted)]"
                    aria-label="Updating Slack account"
                  />
                )}
              </div>

              {account.slack_identity ? (
                <div className="group/slack flex items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2">
                  <CheckCircle2
                    size={13}
                    aria-hidden="true"
                    className="shrink-0 text-[var(--green)]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-[11.5px] font-medium text-[var(--fg-secondary)]">
                        {account.slack_identity.display_name
                          ? `@${account.slack_identity.display_name.replace(/^@/, "")}`
                          : account.slack_identity.slack_user_id}
                      </span>
                      <span className="text-[10.5px] text-[var(--green)]">
                        {account.slack_identity.link_source === "admin" &&
                        !account.slack_identity.display_name &&
                        !account.slack_identity.email
                          ? "Admin exact-ID link"
                          : "Connected"}
                      </span>
                    </div>
                    <div className="truncate font-mono text-[10.5px] text-[var(--fg-muted)]">
                      {account.slack_identity.slack_user_id}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onUnlinkSlack}
                    disabled={busy}
                    aria-label={`Unlink Slack from ${account.name}`}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)] transition-colors disabled:opacity-50 md:opacity-0 md:group-hover/slack:opacity-100 md:focus-visible:opacity-100"
                  >
                    <Unlink2 size={11} aria-hidden="true" />
                    Unlink
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 md:flex-row md:items-start">
                  <div className="min-w-0 flex-1">
                    <label htmlFor={slackInputId} className="sr-only">
                      Slack member ID or profile link for {account.name}
                    </label>
                    <input
                      id={slackInputId}
                      value={slackReference}
                      disabled={busy}
                      onChange={(event) => onEditSlackReference(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && normalizedSlackReference) {
                          event.preventDefault();
                          onLinkSlack();
                        }
                      }}
                      aria-describedby={slackHelpId}
                      aria-invalid={
                        slackReference.trim() && !normalizedSlackReference
                          ? true
                          : undefined
                      }
                      autoComplete="off"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="Member ID or Slack profile link"
                      className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 font-mono text-[12px] outline-none focus:border-[var(--accent)] disabled:opacity-50"
                    />
                    <p
                      id={slackHelpId}
                      className="mt-1 text-[10.5px] text-[var(--fg-muted)]"
                    >
                      {slackReference.trim() && !normalizedSlackReference
                        ? "Use a member ID beginning with U or W, or paste a Slack profile link."
                        : "Admin-only exact-ID link. If Slack Connect hides the profile, Pauta stores no guessed name or email."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onLinkSlack}
                    disabled={busy || !normalizedSlackReference}
                    aria-label={`Link Slack to ${account.name}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--fg-secondary)] hover:border-[var(--accent)] hover:bg-[var(--accent-bg)] transition-colors disabled:opacity-50"
                  >
                    <Link2 size={12} aria-hidden="true" />
                    Link
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 border-t border-[var(--border)] pt-3">
            <div className="mb-2 flex items-center gap-1.5">
              <Users size={12} className="text-[var(--fg-tertiary)]" />
              <span className="text-[10.5px] font-medium text-[var(--fg-tertiary)]">
                Teams
              </span>
              {busyAction === "teams" && (
                <Loader2
                  size={11}
                  className="animate-spin text-[var(--fg-muted)]"
                  aria-label="Saving teams"
                />
              )}
            </div>
            {teams.length > 0 ? (
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-label={`Teams for ${account.name}`}
              >
                {teams.map((team) => {
                  const selected = account.team_ids.includes(team.id);
                  return (
                    <button
                      key={team.id}
                      type="button"
                      aria-pressed={selected}
                      disabled={busy}
                      onClick={() =>
                        onChangeTeams(
                          selected
                            ? account.team_ids.filter((teamId) => teamId !== team.id)
                            : [...account.team_ids, team.id]
                        )
                      }
                      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px] font-medium transition-colors disabled:opacity-50 ${
                        selected
                          ? "border-[var(--accent)] bg-[var(--accent-bg)] text-[var(--fg)]"
                          : "border-[var(--border)] bg-[var(--bg-secondary)] text-[var(--fg-tertiary)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)]"
                      }`}
                    >
                      <span aria-hidden="true" className="text-[11px]">
                        {team.icon}
                      </span>
                      {team.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-[10.5px] text-[var(--fg-muted)]">
                No teams in this workspace.
              </p>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
            {account.can_send_setup && (
              <button
                type="button"
                onClick={onSendSetup}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--fg-secondary)] hover:border-[var(--accent)] hover:bg-[var(--accent-bg)] transition-colors disabled:opacity-50"
              >
                {busyAction === "activation" ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Send size={12} />
                )}
                {account.activation_sent_at ? "Resend setup" : "Send setup"}
              </button>
            )}
            {account.can_send_reset && (
              <button
                type="button"
                onClick={onSendPasswordReset}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--fg-secondary)] hover:border-[var(--accent)] hover:bg-[var(--accent-bg)] transition-colors disabled:opacity-50"
              >
                {busyAction === "password-reset" ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <LockKeyhole size={12} />
                )}
                Send password reset
              </button>
            )}
            {!isOwner && (
              <button
                type="button"
                onClick={onRemove}
                disabled={busy}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[var(--red)]/30 px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--red)] hover:bg-[var(--red)]/10 transition-colors disabled:opacity-50"
              >
                {busyAction === "remove" ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Trash2 size={12} />
                )}
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function WorkspaceAccountAdmin({
  workspaceId,
  refreshKey = 0,
}: {
  workspaceId: string;
  refreshKey?: number;
}) {
  const refreshWorkspaceMembers = useStore((state) => state.refreshWorkspaceMembers);
  const allTeams = useStore((state) => state.teams);
  const teams = allTeams.filter((team) => team.workspaceId === workspaceId);
  const [accounts, setAccounts] = useState<WorkspaceAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Exclude<WorkspaceHumanRole, "owner">>("member");
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [slackReference, setSlackReference] = useState("");
  const [busy, setBusy] = useState<{ userId: string; action: BusyAction } | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/workspaces/${workspaceId}/accounts`, {
        cache: "no-store",
      });
      const data = (await response.json().catch(() => ({}))) as {
        accounts?: WorkspaceAccount[];
        error?: string;
        detail?: string;
      };
      if (!response.ok) {
        setFlash({
          kind: "error",
          message: accountErrorMessage(data.error, data.detail),
        });
        return;
      }
      setAccounts(data.accounts ?? []);
    } catch {
      setFlash({
        kind: "error",
        message: "Pauta could not load workspace accounts. Check your connection and retry.",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts, refreshKey]);

  function closeCreate() {
    setShowCreate(false);
    setName("");
    setEmail("");
    setRole("member");
  }

  function toggleAccount(account: WorkspaceAccount) {
    if (expandedAccountId === account.user_id) {
      setExpandedAccountId(null);
      setSlackReference("");
      return;
    }
    setExpandedAccountId(account.user_id);
    setEditName(account.name);
    setEditEmail(account.email);
    setSlackReference("");
  }

  async function refreshAfterMutation() {
    await Promise.all([loadAccounts(), refreshWorkspaceMembers(workspaceId)]);
  }

  async function createAccount() {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedName = name.trim();
    if (!normalizedName || !normalizedEmail || busy) return;

    setBusy({ userId: "new", action: "create" });
    setFlash(null);
    try {
      const response = await fetch(`/api/workspaces/${workspaceId}/accounts`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: normalizedName, email: normalizedEmail, role }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        detail?: string;
      };
      if (!response.ok || !data.ok) {
        setFlash({ kind: "error", message: accountErrorMessage(data.error, data.detail) });
        return;
      }

      closeCreate();
      setFlash({
        kind: "success",
        message: `${normalizedName} was added silently and can now receive Tasks. No email was sent.`,
      });
      await refreshAfterMutation();
    } catch {
      setFlash({ kind: "error", message: "Pauta could not add this person. Try again." });
    } finally {
      setBusy(null);
    }
  }

  async function updateAccount(
    account: WorkspaceAccount,
    patch: {
      name?: string;
      email?: string;
      role?: Exclude<WorkspaceHumanRole, "owner">;
      team_ids?: string[];
    },
    action: "details" | "role" | "teams"
  ) {
    setBusy({ userId: account.user_id, action });
    setFlash(null);
    try {
      const response = await fetch(
        `/api/workspaces/${workspaceId}/accounts/${account.user_id}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(patch),
        }
      );
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        detail?: string;
      };
      if (!response.ok || !data.ok) {
        setFlash({ kind: "error", message: accountErrorMessage(data.error, data.detail) });
        return;
      }
      setFlash({
        kind: "success",
        message:
          action === "role"
            ? `${account.name}'s access was updated.`
            : action === "teams"
              ? `${account.name}'s teams were updated.`
            : `${account.name}'s account details were updated.`,
      });
      await refreshAfterMutation();
    } catch {
      setFlash({ kind: "error", message: "Pauta could not update this account. Try again." });
    } finally {
      setBusy(null);
    }
  }

  async function sendAccountEmail(
    account: WorkspaceAccount,
    kind: "activation" | "password-reset"
  ) {
    const label = kind === "activation" ? "setup" : "password reset";
    if (!window.confirm(`Send a ${label} email to ${account.email}?`)) return;

    setBusy({ userId: account.user_id, action: kind });
    setFlash(null);
    try {
      const response = await fetch(
        `/api/workspaces/${workspaceId}/accounts/${account.user_id}/${kind}`,
        { method: "POST" }
      );
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        email_sent?: boolean;
        email_error?: string | null;
        error?: string;
        detail?: string;
      };
      if (!response.ok || !data.ok || !data.email_sent) {
        setFlash({
          kind: "error",
          message: data.email_error
            ? `The account is unchanged, but Pauta could not deliver the email: ${data.email_error}`
            : accountErrorMessage(data.error, data.detail),
        });
        return;
      }
      setFlash({
        kind: "success",
        message:
          kind === "activation"
            ? `Setup email sent to ${account.email}.`
            : `Password-reset email sent to ${account.email}.`,
      });
      await loadAccounts();
    } catch {
      setFlash({ kind: "error", message: `Pauta could not send the ${label} email. Try again.` });
    } finally {
      setBusy(null);
    }
  }

  async function linkSlackAccount(account: WorkspaceAccount) {
    const slackUserId = normalizeSlackMemberReference(slackReference);
    if (!slackUserId || busy) return;

    setBusy({ userId: account.user_id, action: "slack" });
    setFlash(null);
    try {
      type SlackLinkResponse = {
        ok?: boolean;
        verification?: AdminSlackLinkVerification;
        error?: string;
        detail?: string;
      };
      const submitLink = async (confirmAdminExactId: boolean) => {
        const response = await fetch(
          `/api/workspaces/${workspaceId}/accounts/${account.user_id}/slack`,
          {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              slack_member: slackUserId,
              ...(confirmAdminExactId
                ? { confirm_admin_exact_id: true }
                : {}),
            }),
          }
        );
        const data = (await response.json().catch(() => ({}))) as SlackLinkResponse;
        return { response, data };
      };

      let { response, data } = await submitLink(false);
      if (
        response.status === 409 &&
        data.error === "slack_exact_id_confirmation_required"
      ) {
        const confirmed = window.confirm(
          `Slack could not verify ${slackUserId}. This can happen for Slack Connect members, but it can also mean the ID is mistyped. Link this exact ID to ${account.name} anyway?`
        );
        if (!confirmed) return;
        ({ response, data } = await submitLink(true));
      }
      if (!response.ok || !data.ok) {
        setFlash({ kind: "error", message: accountErrorMessage(data.error, data.detail) });
        return;
      }
      setSlackReference("");
      setFlash({
        kind: "success",
        message:
          data.verification === "admin_exact_id"
            ? `Admin exact-ID link created for ${slackUserId} and ${account.name}. Slack exposed no profile, so Pauta stored no inferred name or email.`
            : `Slack member ${slackUserId} is now linked to ${account.name}.`,
      });
      await loadAccounts();
    } catch {
      setFlash({
        kind: "error",
        message: "Pauta could not link this Slack account. Try again.",
      });
    } finally {
      setBusy(null);
    }
  }

  async function unlinkSlackAccount(account: WorkspaceAccount) {
    if (!account.slack_identity || busy) return;
    if (
      !window.confirm(
        `Unlink Slack from ${account.name}? Slack actions will no longer be attributed to this Pauta account.`
      )
    ) {
      return;
    }

    setBusy({ userId: account.user_id, action: "slack" });
    setFlash(null);
    try {
      const response = await fetch(
        `/api/workspaces/${workspaceId}/accounts/${account.user_id}/slack`,
        { method: "DELETE" }
      );
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        detail?: string;
      };
      if (!response.ok || !data.ok) {
        setFlash({ kind: "error", message: accountErrorMessage(data.error, data.detail) });
        return;
      }
      setFlash({
        kind: "success",
        message: `Slack was unlinked from ${account.name}.`,
      });
      await loadAccounts();
    } catch {
      setFlash({
        kind: "error",
        message: "Pauta could not unlink this Slack account. Try again.",
      });
    } finally {
      setBusy(null);
    }
  }

  async function removeAccount(account: WorkspaceAccount) {
    if (
      !window.confirm(
        `Remove ${account.name} from this workspace? They will lose access immediately, but their past work stays attributed to them.`
      )
    )
      return;

    setBusy({ userId: account.user_id, action: "remove" });
    setFlash(null);
    try {
      const response = await fetch(
        `/api/workspaces/${workspaceId}/members/${account.user_id}`,
        { method: "DELETE" }
      );
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        detail?: string;
      };
      if (!response.ok || !data.ok) {
        setFlash({ kind: "error", message: accountErrorMessage(data.error, data.detail) });
        return;
      }
      setExpandedAccountId(null);
      setFlash({ kind: "success", message: `${account.name} was removed from this workspace.` });
      await refreshAfterMutation();
    } catch {
      setFlash({ kind: "error", message: "Pauta could not remove this person. Try again." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="px-5 pt-4">
      <div className="mb-2 flex items-center gap-2">
        <UserPlus size={12} className="text-[var(--fg-tertiary)]" />
        <h4 className="text-[11px] font-medium uppercase tracking-wider text-[var(--fg-tertiary)]">
          People
        </h4>
        <span className="text-[11px] text-[var(--fg-muted)]">{accounts.length}</span>
      </div>

      {showCreate ? (
        <div className="mb-3 rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] p-3">
          <div className="mb-3">
            <div className="text-[13px] font-medium">Add a person</div>
            <p className="mt-0.5 text-[11.5px] text-[var(--fg-tertiary)]">
              They can receive Tasks immediately. Pauta will not email them until you send setup.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)_132px]">
            <label className="block min-w-0">
              <span className="mb-1 block text-[10.5px] font-medium text-[var(--fg-tertiary)]">
                Name
              </span>
              <input
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Full name"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
              />
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block text-[10.5px] font-medium text-[var(--fg-tertiary)]">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && void createAccount()}
                placeholder="name@company.com"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] font-medium text-[var(--fg-tertiary)]">
                Access
              </span>
              <select
                value={role}
                onChange={(event) =>
                  setRole(event.target.value as Exclude<WorkspaceHumanRole, "owner">)
                }
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1.5 text-[12px] outline-none focus:border-[var(--accent)]"
              >
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          </div>
          <div className="mt-2 text-[10.5px] text-[var(--fg-muted)]">
            {ROLE_DESCRIPTIONS[role]}
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeCreate}
              className="rounded-md px-2.5 py-1.5 text-[12px] font-medium text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void createAccount()}
              disabled={!name.trim() || !email.trim() || busy !== null}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 transition-colors"
            >
              {busy?.action === "create" ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <UserPlus size={12} />
              )}
              Add silently
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="mb-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-[var(--border-strong)] px-3 py-2 text-[12.5px] font-medium text-[var(--fg-secondary)] hover:border-[var(--accent)] hover:bg-[var(--bg-hover)] transition-colors"
        >
          <UserPlus size={13} />
          Add people
        </button>
      )}

      {flash && (
        <div
          role={flash.kind === "error" ? "alert" : "status"}
          className={`mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-[11.5px] ${
            flash.kind === "success"
              ? "border-[var(--green)]/30 bg-[var(--green)]/10 text-[var(--green)]"
              : "border-[var(--red)]/30 bg-[var(--red)]/10 text-[var(--red)]"
          }`}
        >
          {flash.kind === "success" ? (
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
          ) : (
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
          )}
          <span className="flex-1">{flash.message}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="rounded p-0.5 hover:bg-[var(--bg-hover)] transition-colors"
            aria-label="Dismiss message"
          >
            <X size={11} />
          </button>
        </div>
      )}

      <div className="space-y-2 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-[12.5px] text-[var(--fg-muted)]">
            <Loader2 size={14} className="mr-2 animate-spin" /> Loading people…
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-md border border-[var(--border)] px-3 py-4 text-center text-[12px] text-[var(--fg-muted)]">
            No people have been added yet.
          </div>
        ) : (
          accounts.map((account) => {
            const accountBusy = busy?.userId === account.user_id ? busy.action : null;
            return (
              <WorkspaceAccountRow
                key={account.user_id}
                account={account}
                expanded={expandedAccountId === account.user_id}
                busyAction={accountBusy}
                editName={
                  expandedAccountId === account.user_id ? editName : account.name
                }
                editEmail={
                  expandedAccountId === account.user_id ? editEmail : account.email
                }
                slackReference={
                  expandedAccountId === account.user_id ? slackReference : ""
                }
                teams={teams}
                onToggle={() => toggleAccount(account)}
                onEditName={setEditName}
                onEditEmail={setEditEmail}
                onEditSlackReference={setSlackReference}
                onSaveDetails={() => {
                  const normalizedEmail = editEmail.trim().toLowerCase();
                  const normalizedName = editName.trim();
                  const emailChanged = normalizedEmail !== account.email;
                  const prompt = emailChanged
                    ? `Change ${account.name}'s sign-in email to ${normalizedEmail}? Future setup and sign-in emails will use the new address.`
                    : `Save ${account.name}'s account details?`;
                  if (!window.confirm(prompt)) return;
                  void updateAccount(
                    account,
                    { name: normalizedName, ...(emailChanged ? { email: normalizedEmail } : {}) },
                    "details"
                  );
                }}
                onChangeRole={(nextRole) => {
                  if (nextRole === account.role) return;
                  if (
                    !window.confirm(
                      `Change ${account.name}'s access from ${ROLE_LABELS[account.role]} to ${ROLE_LABELS[nextRole]}?`
                    )
                  )
                    return;
                  void updateAccount(account, { role: nextRole }, "role");
                }}
                onChangeTeams={(teamIds) =>
                  void updateAccount(account, { team_ids: teamIds }, "teams")
                }
                onLinkSlack={() => void linkSlackAccount(account)}
                onUnlinkSlack={() => void unlinkSlackAccount(account)}
                onSendSetup={() => void sendAccountEmail(account, "activation")}
                onSendPasswordReset={() =>
                  void sendAccountEmail(account, "password-reset")
                }
                onRemove={() => void removeAccount(account)}
              />
            );
          })
        )}
      </div>
    </section>
  );
}
